import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ExternalLink, ScanSearch, Send } from "lucide-react";
import type { PageContext } from "../App";
import { api, getApiError } from "../api";
import { buildAttributeFix, fieldInputValue, formatAttributeValue } from "../attributeFix";
import { sellerCentralInventoryUrl } from "../sellerCentral";
import type { AttributeFixContext, AttributeFixField, ContentQualityIssue, ListingPreview } from "../types";
import { Button, InlineError, Modal, StatePanel, formatDate } from "./ui";

function FieldEditor({ field, value, edited, disabled, onChange }: {
  field: AttributeFixField;
  value: string;
  edited: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const options = field.options.length ? field.options : field.valueType === "boolean"
    ? [{ value: true, label: "Ja" }, { value: false, label: "Nein" }] : [];
  const inputId = `attribute-fix-${field.name}`;
  const hints = [
    field.constraints.minLength != null ? `Mindestens ${field.constraints.minLength} Zeichen` : null,
    field.constraints.maxLength != null ? `Höchstens ${field.constraints.maxLength} Zeichen` : null,
    field.constraints.minimum != null ? `Ab ${field.constraints.minimum}` : null,
    field.constraints.maximum != null ? `Bis ${field.constraints.maximum}` : null,
    field.valueType === "integer" ? "Ganze Zahl" : field.valueType === "number" ? "Zahl (Dezimalkomma möglich)" : null,
  ].filter(Boolean);
  const selectedOption = options.findIndex(option => edited
    ? String(option.value) === value : option.value === field.inputValue);
  return <section className="guided-fix-field">
    <h3>{field.label}</h3>
    <p className="guided-fix-field__current"><strong>Aktuell bei Amazon:</strong>{" "}
      {field.currentValueKnown ? formatAttributeValue(field.currentValue) : "Kein Wert geliefert"}
    </p>
    {!field.editable ? <p className="guided-fix-field__reason">{field.reason || "Dieses Feld bitte in Seller Central prüfen."}</p> : <>
      <label className="field" htmlFor={inputId}>
        <span className="field__label">{options.length ? "Zulässigen Wert auswählen" : "Neuen Wert eintragen"}</span>
        {options.length ? <select id={inputId} className="input" value={selectedOption >= 0 ? String(selectedOption) : value === "" ? "" : "current"} disabled={disabled} onChange={event => onChange(String(options[Number(event.target.value)].value))}>
          <option value="" disabled>Bitte auswählen</option>
          {value !== "" && selectedOption < 0 && <option value="current" disabled>Aktueller Wert passt nicht zur Auswahl: {value}</option>}
          {options.map((option, index) => <option key={index} value={String(index)}>{option.label}{String(option.value) !== option.label ? ` (${String(option.value)})` : ""}</option>)}
        </select> : <input id={inputId} className="input" value={value} disabled={disabled}
          inputMode={field.valueType === "integer" ? "numeric" : field.valueType === "number" ? "decimal" : "text"}
          onChange={event => onChange(event.target.value)} placeholder={field.valueType === "string" ? "Passende Produktangabe eintragen" : "Passenden Zahlenwert eintragen"} />}
        {hints.length > 0 && <span className="field__hint">{hints.join(" · ")}</span>}
      </label>
      {options.length > 0 && <p className="field__hint">Auswahl aus den aktuellen Amazon-Feldvorgaben. Wähle den Wert, der tatsächlich zu diesem Artikel passt.</p>}
    </>}
    <details className="task-technical"><summary>Feldbeschreibung und Originalwert</summary>
      <code>{field.name}</code>
      {field.description && <p>{field.description}</p>}
      {field.constraints.pattern && <p>Erforderliches Format: <code>{field.constraints.pattern}</code></p>}
      <pre>{field.currentValueKnown ? JSON.stringify(field.currentValue, null, 2) : "Kein Wert geliefert"}</pre>
    </details>
  </section>;
}

export function AttributeFixDialog({ open, issue, onClose, notify }: {
  open: boolean;
  issue: ContentQualityIssue;
  onClose: () => void;
  notify: PageContext["notify"];
}) {
  const [context, setContext] = useState<AttributeFixContext | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ListingPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const version = ++generation.current;
    setContext(null);
    setValues({});
    setPreview(null);
    setError(null);
    setBusy(false);
    setLoading(open);
    if (open) {
      void api.attributeFixContext(issue.id).then(result => {
        if (version !== generation.current) return;
        setContext(result);
      }).catch(reason => {
        if (version === generation.current) setError(getApiError(reason));
      }).finally(() => {
        if (version === generation.current) setLoading(false);
      });
    }
    return () => { generation.current += 1; };
  }, [open, issue.id, reload]);

  const buildPreview = async () => {
    if (!context || busy || loading) return;
    setError(null);
    let attributes: Record<string, unknown>;
    try {
      attributes = buildAttributeFix(context.fields, values);
    } catch (reason) {
      setError(getApiError(reason));
      return;
    }
    const version = generation.current;
    setBusy(true);
    try {
      const result = await api.previewAttributeFix({ sellerSku: issue.sellerSku, marketplace: issue.marketplaceCode, attributes });
      if (version === generation.current) setPreview(result);
    } catch (reason) {
      if (version === generation.current) setError(getApiError(reason));
    } finally {
      if (version === generation.current) setBusy(false);
    }
  };

  const confirm = async () => {
    if (!preview || busy || !preview.canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.submitListing({
        previewId: preview.previewId,
        confirmationToken: preview.confirmationToken,
        idempotencyKey: globalThis.crypto?.randomUUID?.() ?? `fix-${Date.now()}-${issue.sellerSku}`,
      });
      notify(`Korrektur zur Übermittlung vorgemerkt · Job ${result.jobId}. Der anschließende Live-Abgleich prüft das Ergebnis.`, "success");
      onClose();
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setBusy(false);
    }
  };

  const diff = preview?.marketplaces?.[0]?.diff ?? [];
  const errors = [...(preview?.errors ?? []), ...(preview?.marketplaces?.flatMap(entry => entry.errors ?? []) ?? [])];
  const warnings = [...new Set([...(preview?.warnings ?? []), ...(preview?.marketplaces?.flatMap(entry => entry.warnings ?? []) ?? [])])];
  const editable = context?.fields.some(field => field.editable);

  return <Modal open={open} size="large" title={`Angaben korrigieren · ${issue.marketplaceCode}`}
    description={`${issue.sellerSku} · 1. Wert ändern → 2. Vorschau prüfen → 3. Bestätigen`}
    onClose={() => { if (!busy) onClose(); }}
    footer={<>
      <Button variant="ghost" disabled={busy} onClick={onClose}>Schließen</Button>
      {preview ? <Button loading={busy} disabled={!preview.canSubmit || errors.length > 0 || diff.length === 0} icon={<Send size={15} />} onClick={() => void confirm()}>Änderung bestätigen und senden</Button>
        : <Button loading={busy} disabled={loading || !editable} icon={<ScanSearch size={15} />} onClick={() => void buildPreview()}>Änderung prüfen</Button>}
    </>}>
    <section className="guided-fix-original"><strong>Amazon beanstandet</strong><p>{issue.message}</p></section>
    {loading && <StatePanel compact type="loading" title="Aktuelle Angaben und erlaubte Werte werden bei Amazon geladen" />}
    {error && <InlineError message={error} onRetry={!context && !loading ? () => setReload(value => value + 1) : undefined} />}
    {context && <>
      {context.checkedAt && <p className="field__hint">Abfrage vom {formatDate(context.checkedAt)}{context.productType ? ` · Produkttyp ${context.productType}` : ""}</p>}
      {context.warnings.map(warning => <p className="attribute-fix-warning" key={warning}><AlertTriangle size={15} />{warning}</p>)}
      {context.fields.length === 0 && <StatePanel compact type="empty" title="Amazon nennt kein konkretes Feld" description="Die Originalmeldung in Seller Central prüfen und anschließend den Artikel neu abgleichen." />}
      <div className="attribute-fix-fields">{context.fields.map(field => <FieldEditor key={field.name} field={field}
        value={values[field.name] ?? fieldInputValue(field)} edited={Object.hasOwn(values, field.name)} disabled={busy} onChange={value => {
          setValues(current => ({ ...current, [field.name]: value }));
          setPreview(null);
          setError(null);
        }} />)}</div>
    </>}
    {!loading && (!context || context.fields.some(field => !field.editable) || !editable) && <Button variant="secondary" icon={<ExternalLink size={15} />}
      onClick={() => window.open(sellerCentralInventoryUrl(issue.marketplaceCode, issue), "_blank", "noopener,noreferrer")}>Diesen Artikel in Seller Central prüfen</Button>}
    {preview && <section className="attribute-fix-preview">
      <h4>Diese Änderungen werden übermittelt</h4>
      {diff.length === 0 ? <p>Keine Änderung gegenüber dem aktuellen Amazon-Stand erkannt.</p> : <ul className="attribute-fix-diff">{diff.map((entry, index) => {
        const name = (entry.field ?? entry.path ?? "").replace(/^\/attributes\//, "");
        const label = context?.fields.find(field => field.name === name)?.label ?? name;
        return <li key={index}><strong>{label}</strong><span>Vorher: {formatAttributeValue(entry.before)}</span><span>Nachher: {formatAttributeValue(entry.after)}</span>
          <details className="task-technical"><summary>Vollständige Änderung</summary><pre>{JSON.stringify(entry, null, 2)}</pre></details></li>;
      })}</ul>}
      {warnings.map(warning => <p key={warning} className="attribute-fix-warning">{warning}</p>)}
      {errors.map(message => <p key={message} className="attribute-fix-error">{message}</p>)}
    </section>}
  </Modal>;
}
